import { COMPANY_SCOPE_PREFIX } from "@finway-group/shared/lib/consts/route.constants"
import * as Sentry from "@sentry/react"
import type { ExtractRouteParams } from "react-router"
// eslint-disable-next-line no-restricted-imports -- This is the only file allowed to import generatePath from react-router-dom
import { generatePath as reactRouterGeneratePath } from "react-router-dom"
import { type CompanyScopedRoutePathType, RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { ACCESS_TOKEN } from "~Shared/services/localstorage.consts"
import { decodeAccessToken } from "~Shared/utils/auth.utils"
/**
 * Type-safe generatePath overload for company-scoped routes.
 * Ensures that companyId is always provided from the access token.
 */
export function generatePath<T extends CompanyScopedRoutePathType>(path: T, params: Omit<ExtractRouteParams<T>, "companyId">, overwriteCompanyId?: string): string

export function generatePath<T extends RoutePathEnum>(path: T, params?: ExtractRouteParams<T>): string

export function generatePath(
    path: CompanyScopedRoutePathType | RoutePathEnum,
    params?: Omit<ExtractRouteParams<CompanyScopedRoutePathType | RoutePathEnum>, "companyId">,
    overwriteCompanyId?: string, // don't use unless you know what you are doing
): string {
    // check path is RoutePathEnum
    if (Object.values(RoutePathEnum).includes(path as RoutePathEnum)) {
        return reactRouterGeneratePath(path, params)
    }
    if (overwriteCompanyId) {
        return reactRouterGeneratePath(path, { ...params, companyId: overwriteCompanyId })
    }
    const accessTokenString = localStorage.getItem(ACCESS_TOKEN)
    if (!accessTokenString) {
        return reactRouterGeneratePath(RoutePathEnum.LOGIN)
    }
    const accessToken = accessTokenString ? decodeAccessToken(accessTokenString) : undefined
    const companyId = accessToken?.currentCompanyId
    if (!companyId) {
        return reactRouterGeneratePath(RoutePathEnum.LOGIN)
    }

    return reactRouterGeneratePath(path, { ...params, companyId })
}

export function generateOptionalDetailPath(route: CompanyScopedRoutePathType, id: string | undefined): string | undefined {
    return id ? generatePath(route, { id }) : undefined
}

/** Company id segment from a pathname like `/company/:companyId/...`, if present. */
export function extractCompanyIdFromPathname(pathname: string): string | undefined {
    const prefix = `/${COMPANY_SCOPE_PREFIX}/`
    if (!pathname.startsWith(prefix)) {
        return undefined
    }
    const rest = pathname.slice(prefix.length)
    const slashIdx = rest.indexOf("/")
    const segment = slashIdx === -1 ? rest : rest.slice(0, slashIdx)
    return segment.length > 0 ? segment : undefined
}

/**
 * Router `location.state.from` should be a path string; coerce legacy/mistaken `Location`-like object values.
 *
 * @see https://api.reactrouter.com/v7/interfaces/react-router.Location.html#state-1
 */
export function redirectPathFromState(fromLocationState: unknown): string | undefined {
    if (typeof fromLocationState === "string") {
        return fromLocationState
    }
    if (
        fromLocationState &&
        typeof fromLocationState === "object" &&
        "pathname" in fromLocationState &&
        typeof (fromLocationState as { pathname: unknown }).pathname === "string"
    ) {
        Sentry.captureException(`[redirectPathFromState] Invalid from: ${JSON.stringify(fromLocationState)}`)
        return (fromLocationState as { pathname: string }).pathname
    }
    return undefined
}
